import {
  type ReactNode,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type SVGProps,
  type CSSProperties,
} from 'react';


import { Text } from '../common/Text';
import { Toggle } from '../ui/toggle';
import { View } from '../common/View';

const MenuLine: unique symbol = Symbol('menu-line');
const MenuLabel: unique symbol = Symbol('menu-label');
Menu.line = MenuLine;
Menu.label = MenuLabel;

type KeybindingProps = {
  keyName: ReactNode;
};

function Keybinding({ keyName }: KeybindingProps) {
  return (
    <Text className="text-muted-foreground" style={{ fontSize: 10, marginLeft: 10 }}>
      {keyName}
    </Text>
  );
}

export type MenuItemObject<NameType, Type extends string | symbol = string> = {
  type?: Type;
  name: NameType;
  disabled?: boolean;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  iconSize?: number;
  text: string;
  key?: string;
  toggle?: boolean;
  tooltip?: string;
  submenu?: Array<MenuItem<NameType>>;
};

export type MenuItem<NameType = string> =
  | MenuItemObject<NameType>
  | MenuItemObject<string, typeof Menu.label>
  | typeof Menu.line;

function isLabel<T>(
  item: MenuItemObject<T> | MenuItemObject<string, typeof Menu.label>,
): item is MenuItemObject<string, typeof Menu.label> {
  return item.type === Menu.label;
}

type MenuProps<NameType> = {
  header?: ReactNode;
  footer?: ReactNode;
  items: Array<MenuItem<NameType>>;
  onMenuSelect?: (itemName: NameType) => void;
  style?: CSSProperties;
  className?: string;
  getItemStyle?: (item: MenuItemObject<NameType>) => CSSProperties;
};

export function Menu<const NameType = string>({
  header,
  footer,
  items: allItems,
  onMenuSelect,
  style,
  className,
  getItemStyle,
}: MenuProps<NameType>) {
  const elRef = useRef<HTMLDivElement>(null);
  const items = allItems.filter(x => x);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const submenuLeaveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Trim stale refs when item count shrinks
  itemRefs.current.length = items.length;

  useEffect(() => {
    return () => clearTimeout(submenuLeaveTimer.current);
  }, []);

  useEffect(() => {
    const el = elRef.current;
    el?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      const filteredItems = items.filter(
        item => item && item !== Menu.line && item.type !== Menu.label,
      );
      const currentIndex = filteredItems.indexOf(items[hoveredIndex || 0]);

      const transformIndex = (idx: number) => items.indexOf(filteredItems[idx]);

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setHoveredIndex(
            hoveredIndex === null
              ? 0
              : transformIndex(Math.max(currentIndex - 1, 0)),
          );
          break;
        case 'ArrowDown':
          e.preventDefault();
          setHoveredIndex(
            hoveredIndex === null
              ? 0
              : transformIndex(
                  Math.min(currentIndex + 1, filteredItems.length - 1),
                ),
          );
          break;
        case 'Enter':
          e.preventDefault();
          const item = items[hoveredIndex || 0];
          if (hoveredIndex !== null && item !== Menu.line && !isLabel(item)) {
            onMenuSelect?.(item.name);
          }
          break;
        default:
      }
    };

    el?.addEventListener('keydown', onKeyDown);

    return () => {
      el?.removeEventListener('keydown', onKeyDown);
    };
  }, [hoveredIndex]);

  return (
    <View
      className={className}
      style={{ outline: 'none', borderRadius: 8, overflow: 'hidden', padding: '4px 0', ...style }}
      tabIndex={1}
      innerRef={elRef}
    >
      {header}
      {items.map((item, idx) => {
        if (item === Menu.line) {
          return (
            <View key={idx} style={{ margin: '3px 0px' }}>
              <View style={{ borderTop: '1px solid var(--color-menuBorder)' }} />
            </View>
          );
        } else if (isLabel(item)) {
          return (
            <Text
              key={idx}
              className="text-muted-foreground"
              style={{
                fontSize: 11,
                lineHeight: '1em',
                textTransform: 'uppercase',
                margin: '3px 9px',
                marginTop: 5,
              }}
            >
              {item.name}
            </Text>
          );
        }

        const Icon = item.icon;

        return (
          <View
            role="button"
            key={String(item.name)}
            innerRef={item.submenu ? (el => { itemRefs.current[idx] = el; }) : undefined}
            style={{
              cursor: 'default',
              padding: '6px 10px',
              borderRadius: 6,
              margin: '0 4px',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'var(--color-menuItemText)',
              transition: 'background-color .12s, color .12s',
              ...(item.disabled && { color: 'var(--color-buttonBareDisabledText)' }),
              ...(!item.disabled &&
                hoveredIndex === idx && {
                  backgroundColor: 'var(--color-menuItemBackgroundHover)',
                  color: 'var(--color-menuItemTextHover)',
                }),
              ...(!isLabel(item) && getItemStyle?.(item)),
            }}
            onPointerEnter={() => {
              clearTimeout(submenuLeaveTimer.current);
              setHoveredIndex(idx);
            }}
            onPointerLeave={() => {
              if (item.submenu) {
                submenuLeaveTimer.current = setTimeout(
                  () => setHoveredIndex(null),
                  300,
                );
              } else {
                setHoveredIndex(null);
              }
            }}
            onPointerUp={e => {
              e.stopPropagation();

              if (
                !item.disabled &&
                item.toggle === undefined &&
                !isLabel(item) &&
                !item.submenu
              ) {
                onMenuSelect?.(item.name);
              }
            }}
          >
            {/* Force it to line up evenly */}
            {item.toggle === undefined ? (
              <>
                {Icon && (
                  <Icon
                    width={item.iconSize || 10}
                    height={item.iconSize || 10}
                    style={{ marginRight: 7, width: item.iconSize || 10 }}
                  />
                )}
                <Text title={item.tooltip}>{item.text}</Text>
                <View style={{ flex: 1 }} />
                {item.submenu && (
                  <Text
                    className="text-muted-foreground"
                    style={{
                      fontSize: 12,
                      marginLeft: 4,
                    }}
                  >
                    ›
                  </Text>
                )}
              </>
            ) : (
              <View
                style={{
                  flexDirection: 'row',
                  flex: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <label htmlFor={String(item.name)} title={item.tooltip}>
                  {item.text}
                </label>
                <Toggle
                  id={String(item.name)}
                  isOn={item.toggle}
                  style={{ marginLeft: 5 }}
                  onToggle={() =>
                    !item.disabled &&
                    !isLabel(item) &&
                    item.toggle !== undefined &&
                    onMenuSelect?.(item.name)
                  }
                />
              </View>
            )}
            {item.key && <Keybinding keyName={item.key} />}
            {item.submenu && hoveredIndex === idx && (() => {
              const rect = itemRefs.current[idx]?.getBoundingClientRect();
              if (!rect) return null;
              return (
                <View
                  style={{
                    position: 'fixed',
                    left: rect.right + 4,
                    top: rect.top,
                    zIndex: 9999,
                    borderRadius: 4,
                    overflow: 'hidden',
                    backgroundColor: 'var(--color-tooltipBackground)',
                    border: '1px solid var(--color-tooltipBorder)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    userSelect: 'none',
                  }}
                  onPointerEnter={() => clearTimeout(submenuLeaveTimer.current)}
                  onPointerLeave={() => setHoveredIndex(null)}
                >
                  <Menu
                    items={item.submenu}
                    onMenuSelect={name => onMenuSelect?.(name)}
                  />
                </View>
              );
            })()}
          </View>
        );
      })}
      {footer}
    </View>
  );
}
